/**
 * Advanced Report Builder Page
 * Create custom reports, manage templates, and schedule automated report generation
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  FileText, Plus, Download, Calendar, Clock, Mail, Trash2,
  Play, Settings, Search, Filter, BarChart3, PieChart,
  Table, Shield, GraduationCap, AlertTriangle, Target,
  Activity, TrendingUp, CheckCircle, FileEdit, Eye, Copy,
  Loader2, RefreshCw, ChevronRight, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Report type icons and colors
const REPORT_TYPE_CONFIG = {
  compliance: { icon: Shield, color: 'emerald', label: 'Compliance' },
  incident_trend: { icon: TrendingUp, color: 'blue', label: 'Incident Trend' },
  risk_summary: { icon: AlertTriangle, color: 'amber', label: 'Risk Summary' },
  audit_status: { icon: CheckCircle, color: 'purple', label: 'Audit Status' },
  training_status: { icon: GraduationCap, color: 'cyan', label: 'Training' },
  capa_tracking: { icon: Target, color: 'orange', label: 'CAPA Tracking' },
  frms_summary: { icon: Activity, color: 'rose', label: 'FRMS Summary' },
  safety_performance: { icon: BarChart3, color: 'indigo', label: 'Safety Performance' },
  custom: { icon: FileEdit, color: 'gray', label: 'Custom' }
};

// Format icons
const FORMAT_ICONS = {
  pdf: FileText,
  excel: Table,
  word: FileText,
  csv: FileText,
  json: FileText
};

export default function ReportBuilderPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Form states
  const [generateForm, setGenerateForm] = useState({
    format: 'pdf',
    date_range_start: '',
    date_range_end: ''
  });
  
  const [scheduleForm, setScheduleForm] = useState({
    frequency: 'monthly',
    recipients: '',
    format: 'pdf'
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [templatesRes, reportsRes, schedulesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/report-builder/templates`, { headers }),
        fetch(`${API_URL}/api/report-builder/reports?limit=20`, { headers }),
        fetch(`${API_URL}/api/report-builder/schedules`, { headers }),
        fetch(`${API_URL}/api/report-builder/stats/summary`, { headers })
      ]);
      
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setReports(data.reports || []);
      }
      if (schedulesRes.ok) {
        const data = await schedulesRes.json();
        setSchedules(data.schedules || []);
      }
      if (statsRes.ok) setStats(await statsRes.json());
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report builder data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const seedDefaults = async () => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/templates/seed-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        toast.error('Failed to seed templates');
      }
    } catch (error) {
      toast.error('Error seeding templates');
    }
  };

  const generateReport = async () => {
    if (!selectedTemplate) return;
    
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/report-builder/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          format: generateForm.format,
          date_range_start: generateForm.date_range_start || null,
          date_range_end: generateForm.date_range_end || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Report generated: ${data.report_id}`);
        setShowGenerateDialog(false);
        fetchData();
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      toast.error('Error generating report');
    } finally {
      setGenerating(false);
    }
  };

  const createSchedule = async () => {
    if (!selectedTemplate) return;
    
    try {
      const recipients = scheduleForm.recipients.split(',').map(e => e.trim()).filter(e => e);
      
      const res = await fetch(`${API_URL}/api/report-builder/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          frequency: scheduleForm.frequency,
          recipients,
          format: scheduleForm.format
        })
      });
      
      if (res.ok) {
        toast.success('Schedule created');
        setShowScheduleDialog(false);
        fetchData();
      } else {
        toast.error('Failed to create schedule');
      }
    } catch (error) {
      toast.error('Error creating schedule');
    }
  };

  const downloadReport = async (reportId, format) => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/reports/${reportId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        // Get the blob from response
        const blob = await res.blob();
        
        // Get filename from Content-Disposition header or generate one
        const contentDisposition = res.headers.get('Content-Disposition');
        let filename = `report_${reportId}.${format}`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Report downloaded: ${filename}`);
        fetchData(); // Refresh to update download count
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to download report');
      }
    } catch (error) {
      toast.error('Error downloading report');
    }
  };

  const toggleSchedule = async (scheduleId) => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/schedules/${scheduleId}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Schedule updated');
        fetchData();
      }
    } catch (error) {
      toast.error('Error updating schedule');
    }
  };

  const deleteSchedule = async (scheduleId) => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Schedule deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Error deleting schedule');
    }
  };

  const filteredTemplates = templates.filter(t => 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="report-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">{t('reports.builder')}</h1>
          <p className="text-slate-400 mt-1">{t('reports.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seedDefaults}>
            <Sparkles className="w-4 h-4 mr-2" /> Seed {t('reports.templates')}
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> {t('common.create')} {t('reports.templates')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">{t('reports.templates')}</p>
                <p className="text-2xl font-bold text-white">{stats?.templates?.total || 0}</p>
                <p className="text-xs text-slate-500">{stats?.templates?.system || 0} system, {stats?.templates?.custom || 0} {t('reports.custom')}</p>
              </div>
              <FileText className="w-10 h-10 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">{t('reports.title')}</p>
                <p className="text-2xl font-bold text-white">{stats?.reports?.total || 0}</p>
                <p className="text-xs text-slate-500">{stats?.reports?.this_month || 0} this month</p>
              </div>
              <BarChart3 className="w-10 h-10 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">{t('common.active')} {t('reports.schedule')}</p>
                <p className="text-2xl font-bold text-white">{stats?.schedules?.active || 0}</p>
                <p className="text-xs text-slate-500">Automated reports</p>
              </div>
              <Calendar className="w-10 h-10 text-purple-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">{t('common.export')}</p>
                <p className="text-2xl font-bold text-white">5</p>
                <p className="text-xs text-slate-500">PDF, Excel, Word, CSV, JSON</p>
              </div>
              <Download className="w-10 h-10 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700">
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="reports">Generated Reports</TabsTrigger>
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search templates..."
                className="pl-10 bg-slate-800/50 border-slate-700"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map(template => {
              const config = REPORT_TYPE_CONFIG[template.report_type] || REPORT_TYPE_CONFIG.custom;
              const Icon = config.icon;
              
              return (
                <Card 
                  key={template.id} 
                  className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
                  data-testid={`template-${template.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className={`p-2 rounded-lg bg-${config.color}-500/20`}>
                        <Icon className={`w-5 h-5 text-${config.color}-400`} />
                      </div>
                      <div className="flex gap-1">
                        {template.is_system && (
                          <Badge variant="outline" className="text-xs">System</Badge>
                        )}
                        {template.regulatory_framework && (
                          <Badge className="bg-blue-500/20 text-blue-300 text-xs">
                            {template.regulatory_framework}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-white text-lg mt-2">{template.name}</CardTitle>
                    <CardDescription className="text-slate-400 text-sm line-clamp-2">
                      {template.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                      <span>{template.sections?.length || 0} sections</span>
                      <span>{template.usage_count || 0} generated</span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          setSelectedTemplate(template);
                          setShowGenerateDialog(true);
                        }}
                      >
                        <Play className="w-3 h-3 mr-1" /> Generate
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedTemplate(template);
                          setShowScheduleDialog(true);
                        }}
                      >
                        <Calendar className="w-3 h-3 mr-1" /> Schedule
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredTemplates.length === 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Templates Found</h3>
                <p className="text-slate-400 mb-4">Click "Seed Templates" to add default report templates</p>
                <Button onClick={seedDefaults}>
                  <Sparkles className="w-4 h-4 mr-2" /> Seed Default Templates
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Generated Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="text-left p-4 text-slate-400 font-medium">Report</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Type</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Format</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Generated</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map(report => {
                      const config = REPORT_TYPE_CONFIG[report.report_type] || REPORT_TYPE_CONFIG.custom;
                      const FormatIcon = FORMAT_ICONS[report.format] || FileText;
                      
                      return (
                        <tr key={report.id} className="border-t border-slate-700">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <config.icon className={`w-5 h-5 text-${config.color}-400`} />
                              <div>
                                <p className="text-white font-medium">{report.template_name}</p>
                                <p className="text-xs text-slate-500">{report.id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={`bg-${config.color}-500/20 text-${config.color}-300`}>
                              {config.label}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2 text-slate-300">
                              <FormatIcon className="w-4 h-4" />
                              {report.format?.toUpperCase()}
                            </div>
                          </td>
                          <td className="p-4 text-slate-400 text-sm">
                            {new Date(report.generated_at).toLocaleString()}
                          </td>
                          <td className="p-4">
                            <Badge className={report.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}>
                              {report.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              disabled={report.status !== 'completed'}
                              onClick={() => downloadReport(report.id, report.format)}
                            >
                              <Download className="w-3 h-3 mr-1" /> Download
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {reports.length === 0 && (
                <div className="p-8 text-center">
                  <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Reports Generated</h3>
                  <p className="text-slate-400">Generate your first report from a template</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="text-left p-4 text-slate-400 font-medium">Template</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Frequency</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Next Run</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Recipients</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map(schedule => (
                      <tr key={schedule.id} className="border-t border-slate-700">
                        <td className="p-4">
                          <p className="text-white font-medium">{schedule.template_name}</p>
                          <p className="text-xs text-slate-500">{schedule.format?.toUpperCase()}</p>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline" className="capitalize">
                            {schedule.frequency}
                          </Badge>
                        </td>
                        <td className="p-4 text-slate-400 text-sm">
                          {new Date(schedule.next_run).toLocaleString()}
                        </td>
                        <td className="p-4 text-slate-400 text-sm">
                          {schedule.recipients?.length || 0} recipients
                        </td>
                        <td className="p-4">
                          <Badge className={schedule.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'}>
                            {schedule.is_active ? 'Active' : 'Paused'}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => toggleSchedule(schedule.id)}
                            >
                              {schedule.is_active ? 'Pause' : 'Resume'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => deleteSchedule(schedule.id)}
                            >
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {schedules.length === 0 && (
                <div className="p-8 text-center">
                  <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Scheduled Reports</h3>
                  <p className="text-slate-400">Create a schedule to automate report generation</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate Report Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Generate Report</DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure and generate a report from "{selectedTemplate?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select value={generateForm.format} onValueChange={(v) => setGenerateForm(prev => ({ ...prev, format: v }))}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="pdf">PDF Document</SelectItem>
                  <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                  <SelectItem value="word">Word Document</SelectItem>
                  <SelectItem value="csv">CSV File</SelectItem>
                  <SelectItem value="json">JSON Data</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date Range Start</Label>
                <Input 
                  type="date" 
                  value={generateForm.date_range_start}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, date_range_start: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Date Range End</Label>
                <Input 
                  type="date" 
                  value={generateForm.date_range_end}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, date_range_end: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
            <Button onClick={generateReport} disabled={generating} className="bg-blue-600 hover:bg-blue-700">
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Generate Report</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Schedule Report</DialogTitle>
            <DialogDescription className="text-slate-400">
              Set up automated generation for "{selectedTemplate?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select value={scheduleForm.frequency} onValueChange={(v) => setScheduleForm(prev => ({ ...prev, frequency: v }))}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select value={scheduleForm.format} onValueChange={(v) => setScheduleForm(prev => ({ ...prev, format: v }))}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="pdf">PDF Document</SelectItem>
                  <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Recipients (comma-separated emails)</Label>
              <Input 
                value={scheduleForm.recipients}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, recipients: e.target.value }))}
                placeholder="email1@example.com, email2@example.com"
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
            <Button onClick={createSchedule} className="bg-purple-600 hover:bg-purple-700">
              <Calendar className="w-4 h-4 mr-2" /> Create Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
